/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import msdyn365Commerce, { ISessionStorageContext, ISessionStorageValue } from '@msdyn365-commerce/core-internal';
import { CookieContext } from './cookie-utils';

/**
 * Context for session storage handling
 *
 * @export
 * @class CookieContext
 */
export class SessionStorageContext implements ISessionStorageContext {
    private _cookieContext: CookieContext;

    constructor(_cookieContext: CookieContext) {
        this._cookieContext = _cookieContext;
    }

    /**
     * Gets the value stored in the session storage.
     * Returns an empty object if no such value exists.
     *
     * @param {string} key - The key string.
     */
    public getDataFromStorage = (key: string): object => {
        if (key) {
            return this.get<object>(key).value || {};
        }
        return {};
    };

    /**
     * Sets a new key pair value in the session storage.
     *
     * @param {string} key - The key string.
     * @param {object} value - The data object.
     */
    public setDataInStorage = (key: string, value: object): void => {
        if (key && value) {
            this.set<object>(key, value);
        }
    };

    /**
     * Gets the checkout cart stored in the session storage.
     * Returns an empty object if no such value exists.
     *
     * @param {string} key - The key string.
     */
    public getCheckoutCartData = (key: string): object => {
        if (key) {
            return key ? this.getDataFromStorage(this.getCheckoutCartKey(key)) : {};
        }
        return {};
    };

    /**
     * Sets checkout cart value in the session storage.
     *
     * @param {string} key - The key string.
     * @param {object} value - The cart data object.
     */
    public setCheckoutCartData = (key: string, value: object): void => {
        if (key && value) {
            this.setDataInStorage(this.getCheckoutCartKey(key), value);
        }
    };

    /**
     * Remove session storage by giving key
     *
     * @param {string} key - The key string.
     */
    public removeDataFromStorage = (key: string): void => {
        if (key) {
            this.remove(key);
        }
    };

    /**
     * Remove checkout cart data from the session storage by giving key
     *
     * @param {string} key - The key string. If key is not provided clear all data from session storage that contais the checkout cart key prefix.
     */
    public removeCheckoutCartData = (key?: string): void => {
        if (key) {
            this.removeDataFromStorage(this.getCheckoutCartKey(key));
        } else {
            const keyList = Object.keys(sessionStorage);
            keyList.map(k => {
                if (k.startsWith(`${msdyn365Commerce.checkoutCartKeyPrefix}-`)) {
                    this.removeDataFromStorage(k);
                }
            });
        }
    };

    /**
     * Get session storage value.
     * If server side, no-op.
     *
     * @param {string} key - The key string
     * @return {ISessionStorageValue} value with status
     */
    private get = <T>(key: string): ISessionStorageValue<T> => {
        if (key && msdyn365Commerce.isBrowser) {
            const objString = sessionStorage.getItem(key);
            if (objString) {
                const obj = JSON.parse(objString);
                return { name: key, value: <T>obj, status: obj ? 'FOUND' : 'NOT_FOUND' };
            }
        }
        return { name: key, value: undefined, status: 'NOT_FOUND' };
    };

    /**
     * Set session storage data.
     * If server side, no-op.
     *
     * @param {string} key - The key string
     * @param {T} value - The session storage value
     */
    private set = <T>(key: string, value: T): void => {
        if (key && msdyn365Commerce.isBrowser) {
            sessionStorage.setItem(key, JSON.stringify(value));
        }
    };

    /**
     * Remove session storage data by giving key
     * If server side, no-op.
     *
     * @param {string} key - The key string
     */
    private remove = (key: string): void => {
        if (key && msdyn365Commerce.isBrowser) {
            sessionStorage.removeItem(key);
        }
    };

    /**
     * Build the checkout cart key for the session storage
     */
    private getCheckoutCartKey = (key: string): string => {
        const checkoutCartCookie = this._cookieContext.getCheckoutCartCookie();
        if (checkoutCartCookie) {
            return `${msdyn365Commerce.checkoutCartKeyPrefix}-${checkoutCartCookie}-${key}`;
        }
        return '';
    };
}
